import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

interface PDFOptions {
  title: string;
  subtitle?: string;
  associationName?: string;
  schoolYear?: string;
  footer?: string;
}

interface TableColumn {
  header: string;
  dataKey: string;
}

interface TableData {
  [key: string]: string | number;
}

export class PDFExporter {
  private doc: jsPDF;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number;
  private currentY: number;

  constructor() {
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.margin = 20;
    this.currentY = this.margin;
  }

  private addHeader(options: PDFOptions) {
    this.doc.setFontSize(18);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(31, 41, 55);

    const titleLines = this.doc.splitTextToSize(options.title, this.pageWidth - 2 * this.margin);
    this.doc.text(titleLines, this.pageWidth / 2, this.currentY, { align: 'center' });
    this.currentY += titleLines.length * 7;

    if (options.subtitle) {
      this.doc.setFontSize(12);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(107, 114, 128);
      this.doc.text(options.subtitle, this.pageWidth / 2, this.currentY, { align: 'center' });
      this.currentY += 7;
    }

    if (options.associationName) {
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'italic');
      this.doc.setTextColor(107, 114, 128);
      this.doc.text(options.associationName, this.pageWidth / 2, this.currentY, { align: 'center' });
      this.currentY += 6;
    }

    if (options.schoolYear) {
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(107, 114, 128);
      this.doc.text(`Année scolaire: ${options.schoolYear}`, this.pageWidth / 2, this.currentY, { align: 'center' });
      this.currentY += 6;
    }

    this.doc.setDrawColor(229, 231, 235);
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
    this.currentY += 10;
  }

  private addFooter() {
    const pageCount = (this.doc as any).internal.getNumberOfPages();

    for (let i = 1; i <= pageCount; i++) {
      this.doc.setPage(i);
      this.doc.setFontSize(8);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(156, 163, 175);

      const date = new Date().toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      this.doc.text(
        `Document généré le ${date}`,
        this.margin,
        this.pageHeight - 10
      );

      this.doc.text(
        `Page ${i} sur ${pageCount}`,
        this.pageWidth - this.margin,
        this.pageHeight - 10,
        { align: 'right' }
      );
    }
  }

  addSection(title: string) {
    if (this.currentY > this.pageHeight - 40) {
      this.doc.addPage();
      this.currentY = this.margin;
    }

    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.setTextColor(31, 41, 55);
    this.doc.text(title, this.margin, this.currentY);
    this.currentY += 8;
  }

  addText(text: string, fontSize: number = 10, isBold: boolean = false) {
    if (this.currentY > this.pageHeight - 30) {
      this.doc.addPage();
      this.currentY = this.margin;
    }

    this.doc.setFontSize(fontSize);
    this.doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    this.doc.setTextColor(55, 65, 81);

    const lines = this.doc.splitTextToSize(text, this.pageWidth - 2 * this.margin);
    this.doc.text(lines, this.margin, this.currentY);
    this.currentY += lines.length * (fontSize * 0.5) + 4;
  }

  addTable(columns: TableColumn[], data: TableData[], options?: any) {
    const cleanData = data.map(row => {
      const cleanRow: any = {};
      columns.forEach(col => {
        const value = row[col.dataKey];
        cleanRow[col.dataKey] = typeof value === 'string' ? value : String(value);
      });
      return cleanRow;
    });

    autoTable(this.doc, {
      startY: this.currentY,
      head: [columns.map(col => col.header)],
      body: cleanData.map(row => columns.map(col => row[col.dataKey])),
      margin: { left: this.margin, right: this.margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
        textColor: [55, 65, 81],
        font: 'helvetica',
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251],
      },
      columnStyles: {
        ...options?.columnStyles
      },
      didDrawPage: (data: any) => {
        this.currentY = data.cursor.y + 10;
      },
      ...options
    });

    this.currentY = (this.doc as any).lastAutoTable.finalY + 10;
  }

  addSummaryBox(items: Array<{ label: string; value: string | number; highlight?: boolean }>) {
    if (this.currentY > this.pageHeight - 50) {
      this.doc.addPage();
      this.currentY = this.margin;
    }

    const boxHeight = items.length * 10 + 10;

    this.doc.setFillColor(249, 250, 251);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 'F');
    this.doc.setDrawColor(229, 231, 235);
    this.doc.rect(this.margin, this.currentY, this.pageWidth - 2 * this.margin, boxHeight, 'S');

    let itemY = this.currentY + 8;
    items.forEach(item => {
      this.doc.setFontSize(10);
      this.doc.setFont('helvetica', 'normal');
      this.doc.setTextColor(107, 114, 128);
      this.doc.text(item.label, this.margin + 5, itemY);

      this.doc.setFont('helvetica', 'bold');
      if (item.highlight) {
        this.doc.setTextColor(59, 130, 246);
      } else {
        this.doc.setTextColor(31, 41, 55);
      }
      this.doc.text(String(item.value), this.pageWidth - this.margin - 5, itemY, { align: 'right' });

      itemY += 10;
    });

    this.currentY += boxHeight + 10;
  }

  generate(options: PDFOptions): void {
    this.addHeader(options);
    this.addFooter();
    this.doc.save(`${options.title.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  }

  getDoc(): jsPDF {
    return this.doc;
  }
}

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatCurrencyForPDF = (value: number): string => {
  const formatted = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formatted} EUR`;
};

export const formatDate = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};
